import { gql } from '@apollo/client';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, AutocompleteConfig } from './generic-autocomplete';

// GraphQL query for customers
const CUSTOMERS_QUERY = gql`
	query CustomersQuery(
		$where: CustomerViewModelFilterInput!
		$first: Int!
		$after: String
		$order: [CustomerViewModelSortInput!]
	) {
		customers(
			first: $first
			after: $after
			where: $where
			order: $order
		) {
				edges {
					node {
						customerId
						displayName
						firmName
						firstName
						lastName
						email
						isCompany
						isActive
						isDeleted
					}
					cursor
				}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

interface Customer {
	customerId: string;
	displayName?: string | null;
	firmName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	isCompany: boolean;
	isActive?: boolean;
	isDeleted?: boolean;
}

interface CustomerAutocompleteProps {
	value?: string;
	onChange: (customerId: string, customer: Customer | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	initialSelectedLabel?: string; // Display label to show when form loads with a selected value
	excludeCustomerId?: string; // Single customer ID to exclude from results
	excludeCustomerIds?: string[]; // Array of customer IDs to exclude from results (e.g., already selected beneficiaries)
	autoFocus?: boolean; // Auto-focus the input on mount
}

const getCustomerDisplayName = (customer: Customer): string => {
	if (customer.displayName) {
		return customer.displayName;
	}

	if (customer.isCompany && customer.firmName) {
		return customer.firmName;
	}

	if (customer.firstName || customer.lastName) {
		return [customer.firstName, customer.lastName]
			.filter(Boolean)
			.join(' ');
	}

	return customer.email || customer.customerId;
};

const getCustomerSecondaryText = (customer: Customer): string => {
	if (customer.isCompany) {
		return customer.email || '';
	}
	return customer.firmName || customer.email || '';
};

// Configuration for customer autocomplete
const customerAutocompleteConfig: AutocompleteConfig<Customer> = {
	query: CUSTOMERS_QUERY,
	queryName: 'customers',
	searchFields: ['firmName', 'firstName', 'lastName', 'email'],
	orderBy: [
		{ firmName: 'ASC' },
		{ firstName: 'ASC' },
		{ lastName: 'ASC' }
	],
	getDisplayName: getCustomerDisplayName,
	getSecondaryText: getCustomerSecondaryText,
	getId: (customer) => customer.customerId,
	pageSize: DEFAULT_PAGE_SIZE,
	// Enable details functionality
	enableDetails: true,
	entityType: 'customers'
};

export const CustomerAutocomplete: React.FC<CustomerAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search customers...',
	disabled = false,
	className = '',
	initialSelectedLabel,
	excludeCustomerId,
	excludeCustomerIds,
	autoFocus = false
}) => {
	// Build exclusion filters based on provided exclusion props
	const buildExclusionFilters = (): Record<string, any> => {
		const filters: Record<string, any>[] = [{ isActive: { eq: true } }];

		// Handle array of IDs to exclude (e.g., already selected beneficiaries)
		if (excludeCustomerIds && excludeCustomerIds.length > 0) {
			// Filter out empty strings and the current value (if any)
			const idsToExclude = excludeCustomerIds.filter(id => id && id.trim() !== '');
			if (idsToExclude.length > 0) {
				filters.push({ customerId: { nin: idsToExclude } });
			}
		}

		// Handle single ID exclusion (e.g., current customer being edited)
		if (excludeCustomerId) {
			filters.push({ customerId: { neq: excludeCustomerId } });
		}

		return filters.length === 1 ? filters[0] : { and: filters };
	};

	// Create a config with optional exclusion filter
	const config: AutocompleteConfig<Customer> = {
		...customerAutocompleteConfig,
		baseFilters: buildExclusionFilters()
	};

	return (
		<GenericAutocomplete<Customer>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			initialSelectedLabel={initialSelectedLabel}
			autoFocus={autoFocus}
		/>
	);
};
